import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LayoutGrid, Star } from "lucide-react";
import { api } from "../api/client";
import { backgroundStyle } from "../util/backgrounds";

/** How many boards to show when the user hasn't starred any. */
const FALLBACK_LIMIT = 5;

/**
 * A small board icon (beside the board title) that opens a styled popup for
 * quickly jumping to another board. Shows the user's starred boards if they
 * have any, otherwise the first few boards. Entries reuse the boards-page card
 * look: a background-preview strip, the title, and a star indicator.
 *
 * Dismisses on outside-click and on Escape (scoped to the popup subtree, so a
 * surrounding modal's Escape stack is left alone unless the popup is focused).
 */
export function BoardSwitcher({ currentBoardId }: { currentBoardId: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // only fetch the list once the popup is first opened
  const { data: boards } = useQuery({
    queryKey: ["boards"],
    queryFn: () => api.boards.list(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const starred = (boards ?? []).filter((b) => b.starred);
  const shown = starred.length > 0 ? starred : (boards ?? []).slice(0, FALLBACK_LIMIT);

  return (
    <div
      ref={ref}
      className="relative"
      onKeyDown={(e) => {
        if (open && e.key === "Escape") {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        aria-label="Switch board"
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch board"
        onClick={() => setOpen((v) => !v)}
        className="rounded p-1.5 leading-none text-slate-500 hover:bg-slate-200 hover:text-slate-700"
      >
        <LayoutGrid size={18} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 z-20 mt-1 w-64 rounded-lg border border-slate-200 bg-[var(--surface)] p-1.5 shadow-lg"
        >
          <div className="px-2 py-1 text-xs font-medium text-slate-400">
            {starred.length > 0 ? "Starred boards" : "Your boards"}
          </div>
          {boards == null ? (
            <div className="px-2 py-2 text-sm text-slate-500">Loading…</div>
          ) : shown.length === 0 ? (
            <div className="px-2 py-2 text-sm text-slate-500">No boards.</div>
          ) : (
            <ul className="max-h-80 space-y-1 overflow-y-auto">
              {shown.map((b) => {
                const current = b.id === currentBoardId;
                return (
                  <li key={b.id}>
                    <Link
                      to={`/boards/${b.id}`}
                      onClick={() => setOpen(false)}
                      aria-current={current ? "true" : undefined}
                      className={`group flex items-center gap-2.5 rounded-md border p-1.5 transition ${
                        current
                          ? "border-blue-400 bg-blue-50"
                          : "border-slate-200 hover:border-blue-400 hover:shadow"
                      }`}
                    >
                      <span
                        className="h-8 w-10 shrink-0 rounded bg-slate-100"
                        style={backgroundStyle(b.background)}
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                        {b.title}
                      </span>
                      {b.starred && (
                        <Star
                          size={13}
                          className="shrink-0 fill-current text-amber-500"
                        />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
